import { fileUrl, API } from "@/lib/api";
import { fallbackFor, variantBadge, variantLabel, categoryBadge, categoryLabel, isAdult, money, COLOR_BADGE } from "@/lib/constants";
import { useAgeGate } from "@/context/AgeGateContext";
import { MapPin, DollarSign, ShieldAlert } from "lucide-react";

export default function PosterCard({ poster, index = 0, onClick, publicSlug, meta }) {
  const { verified, requestVerify } = useAgeGate();
  const adult = isAdult(poster);
  const locked = adult && !verified;

  const img =
    poster.image_paths && poster.image_paths.length
      ? publicSlug
        ? `${API}/public/files/${publicSlug}/${poster.image_paths[0]}`
        : fileUrl(poster.image_paths[0])
      : (meta?.cover_url || fallbackFor(poster.category, index));

  const badgeCls = meta?.color && COLOR_BADGE[meta.color] ? COLOR_BADGE[meta.color] : categoryBadge(poster.category);
  const catText = (meta?.label || categoryLabel(poster.category)).split(" ")[0];

  const gain =
    poster.purchase_price != null && poster.market_value != null
      ? poster.market_value - poster.purchase_price
      : null;

  const handleClick = () => {
    if (locked) return requestVerify();
    onClick && onClick();
  };

  return (
    <button
      onClick={handleClick}
      data-testid="poster-card-item"
      className="group text-left bg-surface border border-stroke hard-shadow overflow-hidden flex flex-col animate-fade-up"
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-ink">
        <img
          src={img}
          alt={locked ? "18+ content hidden" : poster.title}
          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${locked ? "blur-2xl scale-110" : ""}`}
        />
        {locked && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-ink/60 text-center px-3" data-testid="adult-lock-overlay">
            <ShieldAlert className="text-red-400" size={28} />
            <span className="font-display text-3xl text-paper leading-none">18+</span>
            <span className="font-mono text-[10px] uppercase tracking-wider text-red-300">Verify age to view</span>
          </div>
        )}
        <div className="absolute top-2 left-2 flex gap-1 flex-wrap">
          <span className={`font-mono text-[10px] uppercase px-2 py-0.5 border ${badgeCls}`}>
            {catText}
          </span>
          {adult && (
            <span className="font-mono text-[10px] uppercase px-2 py-0.5 border border-red-500/50 bg-red-600/10 text-red-400">18+</span>
          )}
          {!locked && (
            <span className={`font-mono text-[10px] uppercase px-2 py-0.5 border ${variantBadge(poster.variant)}`}>
              {variantLabel(poster.variant)}
            </span>
          )}
          {!locked && poster.framed && (
            <span className="font-mono text-[10px] uppercase px-2 py-0.5 border border-cyan/40 bg-cyan/10 text-cyan">Framed</span>
          )}
        </div>
        {!locked && poster.print_number != null && poster.edition_size != null && (
          <span className="absolute bottom-2 right-2 font-mono text-xs bg-ink/80 text-paper px-2 py-1 border border-stroke">
            {poster.print_number}/{poster.edition_size}
          </span>
        )}
      </div>

      <div className="p-4 flex flex-col gap-1 flex-1">
        <div className="font-mono text-[10px] uppercase tracking-widest text-acid truncate">
          {poster.band || "Unknown Act"}
        </div>
        <div className="font-display text-xl text-paper leading-tight line-clamp-2">{poster.title}</div>
        {poster.poster_artist && (
          <div className="font-mono text-[11px] text-muted-gray truncate">art · {poster.poster_artist}</div>
        )}
        <div className="mt-auto pt-3 flex items-center justify-between text-[11px] font-mono">
          <span className="flex items-center gap-1 text-muted-gray truncate">
            <MapPin size={12} /> {poster.venue_city || "—"}
          </span>
          {gain != null && (
            <span className={`flex items-center gap-0.5 ${gain >= 0 ? "text-acid" : "text-magenta"}`}>
              <DollarSign size={12} />
              {gain >= 0 ? "+" : ""}
              {money(gain).replace("$", "")}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
